import React from "react";

import {
  formatMoneyFull,
  formatPercent,
} from "../../../utils/formatters";

export default function CoverageAdequacyTable({
  scenarios = [],
  settings,
  onAnalyzeScenario,
}) {
  const currency =
    settings?.currency || "USD";

  if (!Array.isArray(scenarios) ||
      scenarios.length === 0) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-500">
        No scenario adequacy data is available.
      </div>
    );
  }

  const orderedScenarios = [...scenarios].sort(
    (first, second) => {
      const firstCapitalAtRisk = Number(
        first?.financial_position?.capital_at_risk ??
          first?.coverage_gap_p95 ??
          0
      );

      const secondCapitalAtRisk = Number(
        second?.financial_position?.capital_at_risk ??
          second?.coverage_gap_p95 ??
          0
      );

      return (
        secondCapitalAtRisk -
        firstCapitalAtRisk
      );
    }
  );

  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-left text-slate-500">
            <th className="px-4 py-3">
              Priority
            </th>
            <th className="px-4 py-3">
              Scenario
            </th>
            <th className="px-4 py-3">
              Asset
            </th>
            <th className="px-4 py-3">
              P95 Exposure
            </th>
            <th className="px-4 py-3">
              Transfer Capacity
            </th>
            <th className="px-4 py-3">
              Capital at Risk
            </th>
            <th className="px-4 py-3">
              Protection
            </th>
            <th className="px-4 py-3">
              Coverages
            </th>
            <th className="px-4 py-3">
              Status
            </th>
            <th className="px-4 py-3">
              Action
            </th>
          </tr>
        </thead>

        <tbody>
          {orderedScenarios.map((scenario, index) => {
            const position =
              scenario.financial_position || {};

            const adequacyStatus = String(
                scenario.adequacy_status || ""
            ).toUpperCase();

            const rowClass =
                adequacyStatus === "UNCOVERED"
                    ? "bg-red-50/30"
                    : adequacyStatus === "UNDERINSURED"
                    ? "bg-orange-50/30"
                    : adequacyStatus === "MARGINAL"
                    ? "bg-amber-50/30"
                    : "";

            return (
              <tr
                key={scenario.scenario_id}
                className={`border-b border-slate-100 hover:bg-slate-50 ${rowClass}`}
              >
                <td className="px-4 py-3">
                  <PriorityBadge
                    position={index + 1}
                    status={scenario.adequacy_status}
                  />
                </td>

                <td className="px-4 py-3">
                  <p className="font-medium text-slate-900">
                    {scenario.scenario_name}
                    {index === 0 && (
                      <span className="mt-1 inline-flex rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-700">
                        Highest Capital at Risk
                      </span>
                    )}
                  </p>

                  <p className="mt-1 text-xs text-slate-500">
                    {scenario.incident_type || "—"}
                  </p>
                </td>

                <td className="px-4 py-3">
                  {scenario.asset_name || "—"}
                </td>

                <td className="px-4 py-3">
                  {formatMoneyFull(
                    position.gross_p95 ??
                      scenario.p95,
                    currency
                  )}
                </td>

                <td className="px-4 py-3">
                  {formatMoneyFull(
                    position.transfer_capacity ??
                      scenario.nominal_coverage_capacity,
                    currency
                  )}
                </td>

                <td className="px-4 py-3">
                    <p
                        className={`font-semibold ${
                        Number(
                            position.capital_at_risk ??
                            scenario.coverage_gap_p95 ??
                            0
                        ) > 0
                            ? "text-red-700"
                            : "text-emerald-700"
                        }`}
                    >
                        {formatMoneyFull(
                        position.capital_at_risk ??
                            scenario.coverage_gap_p95,
                        currency
                        )}
                    </p>
                </td>

                <td className="px-4 py-3">
                    <ProtectionIndicator
                        ratio={
                        position.protection_ratio ??
                        scenario.adequacy_ratio ??
                        0
                        }
                    />
                </td>

                <td className="px-4 py-3">
                  {scenario.active_coverages_count ?? 0}
                </td>

                <td className="px-4 py-3">
                  <AdequacyBadge
                    status={
                      scenario.adequacy_status
                    }
                  />
                </td>

                <td className="px-4 py-3">
                  <button
                    type="button"
                    onClick={() =>
                      onAnalyzeScenario?.(
                        scenario.scenario_id
                      )
                    }
                    className="rounded-md border border-slate-300 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                  >
                    Analyze
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function AdequacyBadge({
  status,
}) {
  const normalized =
    String(status || "UNKNOWN").toUpperCase();

  const config = {
    ADEQUATE: {
      label: "Adequate",
      dot: "bg-emerald-500",
      style:
        "border-emerald-200 bg-emerald-100 text-emerald-800",
    },
    MARGINAL: {
      label: "Marginal",
      dot: "bg-amber-500",
      style:
        "border-amber-200 bg-amber-100 text-amber-800",
    },
    UNDERINSURED: {
      label: "Underinsured",
      dot: "bg-orange-500",
      style:
        "border-orange-200 bg-orange-100 text-orange-800",
    },
    UNCOVERED: {
      label: "Uncovered",
      dot: "bg-red-500",
      style:
        "border-red-200 bg-red-100 text-red-800",
    },
  };

  const current =
    config[normalized] || {
      label: "Unknown",
      dot: "bg-slate-400",
      style:
        "border-slate-200 bg-slate-100 text-slate-700",
    };

  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold ${current.style}`}
    >
      <span
        className={`h-2 w-2 rounded-full ${current.dot}`}
      />

      {current.label}
    </span>
  );
}

function formatAdequacyStatus(value) {
  return String(value || "")
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (character) =>
      character.toUpperCase()
    );
}

function ProtectionIndicator({
  ratio,
}) {
  const normalizedRatio = Math.min(
    Math.max(Number(ratio || 0), 0),
    1
  );

  const percentage =
    normalizedRatio * 100;

  const barClass =
    percentage >= 100
      ? "bg-emerald-500"
      : percentage >= 75
      ? "bg-amber-500"
      : percentage > 0
      ? "bg-orange-500"
      : "bg-red-500";

  return (
    <div className="min-w-[120px]">
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs font-semibold text-slate-700">
          {formatPercent(
            normalizedRatio,
            1
          )}
        </span>
      </div>

      <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-slate-200">
        <div
          className={`h-full rounded-full transition-all ${barClass}`}
          style={{
            width: `${percentage}%`,
          }}
        />
      </div>
    </div>
  );
}

function PriorityBadge({
  position,
  status,
}) {
  const normalizedStatus = String(
    status || ""
  ).toUpperCase();

  const isCritical =
    normalizedStatus === "UNCOVERED" ||
    normalizedStatus === "UNDERINSURED";

  return (
    <span
      className={`inline-flex h-7 min-w-7 items-center justify-center rounded-full px-2 text-xs font-semibold ${
        isCritical
          ? "bg-red-100 text-red-800"
          : "bg-slate-100 text-slate-700"
      }`}
    >
      {position}
    </span>
  );
}